import { useEffect, useState } from "react";
import { retrieveAssetChildren } from "../api";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { HIERARCHY_COLS, HIERARCHY_DEPTH_STEP } from "../hierarchyLayout";

const colors = {
  // Also add border tokens for dark so roleMeta border doesn't break
  dark: {
    tag: "#93c5fd",
    value: "#e2e8f0",
    muted: "#94a3b8",
    badgeBg: "rgba(56, 189, 248, 0.18)",
    badgeText: "#7dd3fc",
    currentBg: "rgba(160, 196, 109, 0.2)",
    currentText: "#A0C46D",
    currentBorder: "rgba(160, 196, 109, 0.45)",
    parentBg: "rgba(148, 163, 184, 0.15)",
    parentText: "#cbd5e1",
    parentBorder: "rgba(148, 163, 184, 0.3)",
    childBg: "rgba(56, 189, 248, 0.15)",
    childText: "#7dd3fc",
    childBorder: "rgba(56, 189, 248, 0.35)",
    btn: "#A0C46D",
    btnBorder: "rgba(160, 196, 109, 0.45)",
    btnHover: "rgba(160, 196, 109, 0.18)",
  },
  light: {
    tag: "#0c4a6e",
    value: "#0f172a",
    muted: "#64748b",
    badgeBg: "#e0f2fe",
    badgeText: "#0369a1",
    currentBg: "#ecfdf3",
    currentText: "#166534",
    currentBorder: "#86efac",
    parentBg: "#f1f5f9",
    parentText: "#334155",
    parentBorder: "#cbd5e1",
    childBg: "#eff6ff",
    childText: "#1d4ed8",
    childBorder: "#bfdbfe",
    btn: "#3f6212",
    btnBorder: "#86efac",
    btnHover: "rgba(134, 239, 172, 0.25)",
  },
};

function roleMeta(role, c) {
  if (role === "current")
    return {
      label: "Current",
      bg: c.currentBg,
      color: c.currentText,
      border: c.currentBorder || "transparent",
    };
  if (role === "parent")
    return {
      label: "Parent",
      bg: c.parentBg,
      color: c.parentText,
      border: c.parentBorder || "transparent",
    };
  return {
    label: "Child",
    bg: c.childBg,
    color: c.childText,
    border: c.childBorder || "transparent",
  };
}

function HierarchyRow({
  role,
  depth = 0,
  assetTag,
  description,
  lastEvent,
  metricLabel,
  metricValue,
  showViewSnapshot,
  onViewSnapshot,
  isDark,
}) {
  const c = colors[isDark ? "dark" : "light"];
  const roleStyle = roleMeta(role, c);
  const desc = description?.trim() || "";

  const row = {
    display: "grid",
    gridTemplateColumns: HIERARCHY_COLS,
    alignItems: "center",
    justifyItems: "stretch",
    gap: "0 6px",
    width: "100%",
    minWidth: 0,
    fontFamily: "Lato, sans-serif",
  };

  return (
    <span style={row}>
      <span
        style={{
          display: "flex",
          alignItems: "center",
          gap: 4,
          minWidth: 0,
          paddingLeft: depth * HIERARCHY_DEPTH_STEP,
        }}
      >
        <span
          style={{
            flexShrink: 0,
            fontSize: "0.55rem",
            fontWeight: 700,
            letterSpacing: "0.02em",
            textTransform: "uppercase",
            padding: "1px 5px",
            borderRadius: 4,
            backgroundColor: roleStyle.bg,
            color: roleStyle.color,
            border: "1px solid " + roleStyle.border,
            lineHeight: 1.15,
          }}
          title={roleStyle.label}
        >
          {roleStyle.label}
        </span>
        <span
          style={{
            color: c.tag,
            fontWeight: 700,
            fontSize: "0.68rem",
            letterSpacing: "0.01em",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
          title={assetTag}
        >
          {assetTag || "—"}
        </span>
      </span>

      <span
        style={{
          color: desc ? c.value : c.muted,
          fontSize: "0.65rem",
          fontWeight: desc ? 500 : 400,
          opacity: desc ? 1 : 0.6,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={desc || undefined}
      >
        {desc || "—"}
      </span>

      <span
        style={{
          color: c.value,
          fontSize: "0.65rem",
          fontWeight: 500,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        }}
        title={lastEvent || undefined}
      >
        {lastEvent || "—"}
      </span>

      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
        }}
        title={
          metricValue != null ? metricLabel + ": " + metricValue : undefined
        }
      >
        {metricValue != null ? (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              minWidth: 20,
              padding: "0 5px",
              borderRadius: 4,
              backgroundColor: c.badgeBg,
              color: c.badgeText,
              fontSize: "0.62rem",
              fontWeight: 700,
              fontVariantNumeric: "tabular-nums",
              lineHeight: 1.4,
            }}
          >
            {metricValue}
          </span>
        ) : null}
      </span>

      <span
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "flex-end",
          width: "100%",
          minHeight: 20,
          boxSizing: "border-box",
        }}
      >
        {showViewSnapshot ? (
          <span
            role="button"
            tabIndex={0}
            aria-label={"View snapshot for " + (assetTag || "")}
            title="View Snapshot"
            style={{
              color: c.btn,
              backgroundColor: "transparent",
              cursor: "pointer",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              width: 22,
              height: 22,
              borderRadius: 5,
              border: "1px solid " + c.btnBorder,
              flexShrink: 0,
              transition: "background-color 0.15s ease, border-color 0.15s ease",
            }}
            onClick={(e) => {
              e.stopPropagation();
              onViewSnapshot?.();
            }}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                e.stopPropagation();
                onViewSnapshot?.();
              }
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = c.btnHover;
              e.currentTarget.style.borderColor = c.btn;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.borderColor = c.btnBorder;
            }}
          >
            <VisibilityIcon sx={{ color: c.btn, fontSize: "0.8rem" }} />
          </span>
        ) : (
          <span
            title="Currently viewing"
            style={{
              fontSize: "0.55rem",
              fontWeight: 700,
              color: c.currentText,
              letterSpacing: "0.02em",
            }}
          >
            NOW
          </span>
        )}
      </span>
    </span>
  );
}

export function useTreeView(props) {
  const {
    apiUrl,
    assetData,
    assetId,
    facilities,
    history,
    isDark = false,
    mounted,
    organizationId,
    token,
    zones,
  } = props;

  const [treeView, setTreeView] = useState();

  useEffect(() => {
    const viewSnapshot = (id, assetMode) => {
      const isInventory =
        assetMode === "Inventory" || assetMode === "inventory";
      history.push(
        isInventory ? `/inventorySnapshot/${id}` : `/assetSnapshot/${id}`
      );
    };

    const recursiveFetch = (rootId) => {
      const fetchChildren = async (id) => {
        const children = await retrieveAssetChildren(
          { organizationId, apiUrl, token, zones, facilities },
          id
        );
        return Promise.all(
          children.map(async (child) => {
            child.children = await fetchChildren(child.id);
            return child;
          })
        );
      };
      return fetchChildren(rootId);
    };

    const collectAllNodeIds = (nodes) => {
      if (!nodes || !Array.isArray(nodes)) return [];
      return nodes.flatMap((n) => [n.id, ...collectAllNodeIds(n.children)]);
    };

    const nodeRole = (id) => {
      if (assetData.assetId === id) return "current";
      if (assetData.ancestors?.includes(id)) return "parent";
      return "child";
    };

    const attachLabels = (nodes, depth) => {
      if (!Array.isArray(nodes)) return;
      nodes.forEach((child) => {
        const role = nodeRole(child.id);
        const isChild = role === "child";
        const mode = String(child.assetMode || "").toLowerCase();
        const isDeviceOrTracker =
          mode === "device" || mode === "tracker";
        const showQty = isChild && !isDeviceOrTracker;

        child.label = (
          <HierarchyRow
            role={role}
            depth={depth}
            assetTag={child.assetTag}
            description={child.description}
            lastEvent={child.lastEvent}
            metricLabel={showQty ? "Quantity Picked" : undefined}
            metricValue={
              showQty
                ? (child.quantityPicked ?? child.quantity_picked ?? 0)
                : null
            }
            showViewSnapshot={assetData.assetId !== child.id}
            onViewSnapshot={() => viewSnapshot(child.id, child.assetMode)}
            isDark={isDark}
          />
        );

        if (child?.children?.length > 0) {
          attachLabels(child.children, depth + 1);
        }
      });
    };

    const generateTreeView = async () => {
      const children = await recursiveFetch(
        assetData.ancestors?.length
          ? assetData.ancestors[assetData.ancestors.length - 1]
          : assetData.assetId
      );

      attachLabels(children, 1);

      const rootAncestorSource =
        assetData?.ancestors?.length > 0 &&
        assetData?.ancestorList?.length > 0
          ? assetData.ancestorList.filter(
              (ancestor) =>
                ancestor.assetId ===
                assetData.ancestors[assetData.ancestors.length - 1]
            )
          : [assetData];

      const asset = rootAncestorSource?.[0];
      if (!asset) return null;

      const hasGpsCoordinates =
        asset.latitude &&
        asset.longitude &&
        (asset.latitude !== 0 || asset.longitude !== 0);

      const rootAncestor = {
        assetTag: asset.tag,
        assetMode: asset.assetMode ?? asset.asset_mode ?? null,
        description:
          asset.propertiesMap?.description ?? asset.description ?? null,
        id: asset.assetId,
        lastEvent: asset.lastEvent,
      };

      const rootRole = nodeRole(rootAncestor.id);

      return {
        id: rootAncestor.id,
        label: (
          <HierarchyRow
            role={rootRole}
            depth={0}
            assetTag={rootAncestor.assetTag}
            description={rootAncestor.description}
            lastEvent={rootAncestor.lastEvent}
            metricLabel={undefined}
            metricValue={null}
            showViewSnapshot={rootAncestor.id !== assetData.assetId}
            onViewSnapshot={() =>
              viewSnapshot(rootAncestor.id, rootAncestor.assetMode)
            }
            isDark={isDark}
          />
        ),
        lastEvent: rootAncestor.lastEvent,
        children,
        defaultSelected: String(assetData.assetId),
        defaultExpanded: [
          rootAncestor.id,
          ...collectAllNodeIds(children),
        ].map(String),
        complete: true,
        facility: hasGpsCoordinates
          ? ""
          : facilities[asset.facilityId]
            ? facilities[asset.facilityId].name
            : "",
      };
    };

    if (mounted && assetData?.assetId) {
      generateTreeView().then((res) => {
        if (res) {
          setTreeView((t) => ({ ...t, ...res, complete: true }));
        }
      });
    }

    return () => {
      setTreeView((prevState) => ({
        ...prevState,
        complete: false,
      }));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    facilities,
    history,
    assetId,
    isDark,
    organizationId,
    apiUrl,
    token,
    zones,
    assetData,
    mounted,
  ]);

  return treeView;
}
